import { useEffect, useState } from 'react';
import { Download, ShieldCheck } from 'lucide-react';
import { MarketingHeroPill } from '@/components/marketing/v2/MarketingHeroPill';
import { NeobrutalButton } from '@/components/marketing/v2/NeobrutalButton';
import { DESKTOP_VERSION, OS_LABEL, type DetectedOs, detectOs } from '@/components/marketing/download/v2/shared';

export default function InstallHeroV2() {
  const [os, setOs] = useState<DetectedOs>('other');

  useEffect(() => {
    setOs(detectOs());
  }, []);

  return (
    <section className="bg-paper pb-14 pt-16 md:pb-20 md:pt-20">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="grid items-center gap-12 md:grid-cols-[1.15fr_1fr] md:gap-14">
          {/* LEFT */}
          <div>
            <MarketingHeroPill>Guide d'installation · Desktop v{DESKTOP_VERSION}</MarketingHeroPill>

            <h1 className="mt-7 font-marketing-display text-[clamp(42px,6.4vw,92px)] font-bold leading-[0.92] tracking-[-0.045em] text-charcoal-800">
              Installe l'app.
              <br />
              <span className="underline-lime">En 30 secondes.</span>
            </h1>

            <p className="mt-7 max-w-xl font-marketing-body text-lg leading-[1.55] text-charcoal-600 md:text-xl">
              L'app desktop n'est pas encore notarisée Apple ni certifiée Microsoft. Ton système
              affiche donc un avertissement au premier lancement. Voici comment l'ouvrir — en toute
              sécurité, en une étape.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <NeobrutalButton as="link" to="/telechargement" variant="primary" size="lg">
                <Download className="h-[18px] w-[18px]" strokeWidth={2.4} />
                Télécharger d'abord
              </NeobrutalButton>
              <a
                href="#install-os-guide"
                className="font-marketing-mono text-[12px] font-medium uppercase tracking-[0.1em] text-charcoal-800 underline decoration-dotted underline-offset-[5px] hover:text-lime-600"
              >
                Aller aux étapes ↓
              </a>
            </div>

            <div className="mt-6 font-marketing-mono text-[11.5px] uppercase tracking-[0.08em] text-charcoal-500">
              Système détecté&nbsp;: <span className="text-charcoal-800">{OS_LABEL[os]}</span>
            </div>
          </div>

          {/* RIGHT — reassurance card */}
          <aside className="border-[3px] border-charcoal-800 bg-charcoal-800 p-7 text-white shadow-neobrutal-lime-10 md:p-8">
            <div className="flex items-center gap-3 border-b border-charcoal-700 pb-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center border-2 border-charcoal-700 bg-charcoal-900 text-lime-500">
                <ShieldCheck className="h-[22px] w-[22px]" strokeWidth={2} />
              </span>
              <span className="font-marketing-mono text-[11px] font-medium uppercase tracking-[0.12em] text-charcoal-200">
                Pourquoi cet avertissement&nbsp;?
              </span>
            </div>
            <ul className="mt-5 space-y-4">
              {[
                'L\'app n\'est pas encore signée par un certificat payant Apple / Microsoft.',
                'Le code est open-source et les binaires sont publiés publiquement sur GitHub Releases.',
                'Une seule étape manuelle au premier lancement, puis plus jamais.',
              ].map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-3 font-marketing-body text-[14.5px] leading-[1.5] text-charcoal-200"
                >
                  <span className="mt-[3px] font-marketing-display font-bold text-lime-500">→</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </section>
  );
}
